import { Checkmark } from '@app/routes/content/routes/library/components/icons/check-mark';
import { ContentRepurposingStatus } from '@app/routes/content/routes/recordings/components/content-composer/domain';
import { Cross } from '@app/routes/content/routes/recordings/components/icons/cross';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React from 'react';

export type ChecklistItemStatus = ContentRepurposingStatus;

export type ChecklistItem = {
  /** Unique identifier for the item */
  id: string;
  /** Status of the checklist item */
  status: ChecklistItemStatus;
  /** Translation key for the status text */
  textKey: string;
};

type ChecklistProps = {
  /** Array of checklist items to display */
  items: ChecklistItem[];
};

type ChecklistItemComponentProps = {
  status: ChecklistItemStatus;
  textKey: string;
};

/**
 * A checklist component for displaying progress steps with status indicators.
 * Uses ContentRepurposingStatus directly from the API.
 *
 * @example
 * <Checklist
 *   items={[
 *     { id: 'tone', status: 'FINISHED', textKey: 'tone-matched' },
 *     { id: 'topics', status: 'IN_PROGRESS', textKey: 'finding-topics' },
 *   ]}
 * />
 */
const Checklist: React.FC<ChecklistProps> = ({ items }) => {
  return (
    <div className="flex flex-col items-start gap-4 animate-fade-in">
      {items.map(item => (
        <ChecklistItemComponent
          key={item.id}
          status={item.status}
          textKey={item.textKey}
        />
      ))}
    </div>
  );
};

const ChecklistItemComponent: React.FC<ChecklistItemComponentProps> = ({
  status,
  textKey,
}) => {
  const { t } = useTranslation();
  const isCompleted = status === 'FINISHED';
  const isFailed = status === 'FAILED';
  // Both INITIAL and IN_PROGRESS are "processing" states
  const isProcessing = status === 'INITIAL' || status === 'IN_PROGRESS';

  return (
    <div className="flex items-center gap-4">
      <div className={cx('relative flex justify-center items-center')}>
        <div
          className={cx(
            'size-5 flex justify-center items-center rounded-full z-10 transition-transform duration-500 ease-out',
            {
              'border border-spring-neutral-b2/90 bg-spring-neutral-base':
                isProcessing,
              'bg-spring-primary scale-[1.15]': isCompleted,
              'bg-red-400': isFailed,
            },
          )}
        >
          {isCompleted && (
            <Checkmark className="size-4 text-spring-neutral-b0" />
          )}
          {isFailed && <Cross className="size-4 text-spring-neutral-b0" />}
        </div>
      </div>
      <div
        key={textKey}
        className={cx(
          'm-0 [line-height:1.25rem] text-left text-spring-neutral-b2/90 transition-opacity ease-out animate-slide-in',
          {
            'text-sm font-medium': isCompleted || isFailed,
            'text-sm font-normal': isProcessing,
          },
        )}
      >
        {t(textKey)}
      </div>
    </div>
  );
};

export { Checklist };
